import {Tag, TagColor, TagSize} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactNode} from 'react';

import {pluralize} from 'BenchlingUtil';
import ChildImportPlanSection from 'config-migration/import/import-plan-sections/ChildImportPlanSection';
import {LogLevel} from 'config-migration/migration-log/LogLevel';
import {HighImpactChange, MigrationError, MigrationObjectPlan} from 'config-migration/MigrationConstants';
import {InlineErrorIcon, InlineWarningIcon} from 'config-migration/shared-ui-components/InlineIcon';
import {MigrationTab} from 'config-migration/types/MigrationTab';
import getTestProps from 'test/lib/cypress/getTestProps';
import * as Colors from 'ui-library/Colors';
import {GRAY_DARK, ORANGE_LIGHTEST, RED_POWDER} from 'ui-library/Colors';
import Popover from 'ui-library/floating-ui/Popover';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {ObjectActionType} from '../../types/objectActions';
import {countChildHighImpactChanges} from '../HighImpactChanges';
import ObjectActionDropdown from '../ObjectActionDropdown';

export interface ImportPlanSectionRowProps {
  plan: MigrationObjectPlan;
  errors: Array<MigrationError>;
  isNested?: boolean;
  /**
   * Controls whether the dropdown is shown and modifiable. If true, the default action is just shown as text.
   */
  showActionInline?: boolean;
  onUpdatePlan: (plan: MigrationObjectPlan) => void;
  highImpactChangeMap: Map<string, Array<HighImpactChange>>;
}

export default function ImportPlanSectionRow(props: ImportPlanSectionRowProps): JSX.Element {
  const {plan, errors} = props;
  const hasErrors = errors.filter(({level}) => level === LogLevel.ERROR).length > 0;
  const hasWarnings = errors.filter(({level}) => level === LogLevel.WARNING).length > 0;
  const hasChildren = Boolean(plan.childMigrationPlans.length + plan.subtractedChildIdentifiers.length);
  const [isCollapsed, setIsCollapsed] = React.useState<boolean>(true);
  const highImpactChangeCount = countChildHighImpactChanges(plan, props.highImpactChangeMap);
  return (
    <>
      <ImportPlanSectionRowWrapper
        plan={plan}
        hasErrors={hasErrors}
        hasWarnings={hasWarnings}
        key={plan.objectId}
        isNested={props.isNested}
      >
        <ObjectDisplayNameColumn
          hasErrors={hasErrors}
          hasWarnings={hasWarnings}
          plan={plan}
          errors={errors}
          setIsCollapsed={setIsCollapsed}
          isCollapsed={isCollapsed}
          hasChildren={hasChildren}
          isNested={props.isNested}
          highImpactChangeCount={highImpactChangeCount}
        />
        <ObjectActionDropdownColumn
          plan={plan}
          onUpdatePlan={props.onUpdatePlan}
          showActionInline={props.showActionInline}
        />
      </ImportPlanSectionRowWrapper>
      {!isCollapsed && hasChildren && (
        <ChildImportPlanSection
          plan={props.plan}
          onUpdatePlan={props.onUpdatePlan}
          isNested={props.isNested}
          highImpactChangeMap={props.highImpactChangeMap}
        />
      )}
    </>
  );
}

export interface ImportPlanSectionRowWrapperProps {
  plan: MigrationObjectPlan;
  hasErrors: boolean;
  hasWarnings: boolean;
  children: ReactNode;
  isNested?: boolean;
}

export function ImportPlanSectionRowWrapper(props: ImportPlanSectionRowWrapperProps): JSX.Element {
  return (
    <div
      className={classNames(
        'simpleDivTable-row',
        props.isNested && css(styles.childRow),
        props.plan.selectedAction.type === ObjectActionType.IGNORE && css(styles.ignoreRow),
        props.hasErrors && css(styles.errorRow),
        props.hasWarnings && !props.hasErrors && css(styles.warningRow)
      )}
      {...getTestProps({component: 'ImportPlanSectionRow', element: 'tableRow'})}
    >
      {props.children}
    </div>
  );
}

export function ObjectDisplayNameColumn(props: {
  hasErrors: boolean;
  hasWarnings: boolean;
  rowIcon?: BenchlingIconName;
  subLabel?: string;
  plan: MigrationObjectPlan;
  errors: Array<MigrationError>;
  setIsCollapsed: (isCollapsed: boolean) => void;
  isCollapsed: boolean;
  hasChildren: boolean;
  isNested?: boolean;
  highImpactChangeCount: number;
}): JSX.Element {
  return (
    <div
      className={classNames(
        'simpleDivTable-cell',
        props.subLabel ? css(styles.subLabelHeight) : null,
        props.hasErrors || props.hasWarnings ? css(styles.iconCell) : css(styles.firstCell),
        'u-flex-display u-vertical-center-items'
      )}
    >
      <div>
        {(() => {
          if (props.hasErrors) {
            return (
              <InlineErrorIcon
                objectName={props.plan.objectDisplayName}
                migrationTab={MigrationTab.IMPORT}
                errors={props.errors}
              />
            );
          } else if (props.hasWarnings) {
            return (
              <InlineWarningIcon
                objectName={props.plan.objectDisplayName}
                migrationTab={MigrationTab.IMPORT}
                errors={props.errors}
              />
            );
          }
          return null;
        })()}
        {props.rowIcon ? <BenchlingIcon className="result-icon" icon={props.rowIcon} /> : null}
        <span
          onClick={() => {
            props.setIsCollapsed(!props.isCollapsed);
          }}
          className={classNames(
            props.hasChildren ? css(styles.parentToggle) : !props.isNested ? css(styles.noToggle) : null
          )}
          {...(props.hasChildren &&
            getTestProps({component: 'ImportPlanSectionRow', element: 'collapseToggle'}))}
        >
          {(() => {
            if (!props.hasChildren) {
              return null;
            }
            if (props.isCollapsed) {
              return <BenchlingIcon className={css(styles.toggleIcon)} icon="triangle-right" />;
            } else {
              return <BenchlingIcon className={css(styles.toggleIcon)} icon="triangle-down" />;
            }
          })()}
          {props.plan.objectDisplayName}
          {props.highImpactChangeCount > 0 && (
            <span className={css(styles.highImpactParentIcon)}>
              <Popover renderContent={() => getParentIconHoverText(props.highImpactChangeCount)}>
                <Tag
                  text={props.highImpactChangeCount.toString()}
                  color={TagColor.WARN}
                  size={TagSize.SMALL}
                />
              </Popover>
            </span>
          )}
        </span>
      </div>
      {props.subLabel ? <div className={css(styles.subLabel)}>{props.subLabel}</div> : null}
    </div>
  );
}

function getParentIconHoverText(highImpactChangeCount: number): string {
  return `${highImpactChangeCount} high impact ${pluralize(highImpactChangeCount, 'change')} to this object`;
}

export function ObjectActionDropdownColumn(props: {
  plan: MigrationObjectPlan;
  onUpdatePlan: (plan: MigrationObjectPlan) => void;
  showActionInline?: boolean;
}): JSX.Element {
  return (
    <div className="simpleDivTable-cell">
      <ObjectActionDropdown
        object={props.plan}
        showActionInline={props.showActionInline}
        onUpdateObject={(plan: MigrationObjectPlan) => {
          props.onUpdatePlan({
            ...plan,
            childMigrationPlans: [],
          });
        }}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  iconCell: {
    marginLeft: -6,
    paddingRight: 18,
  },
  firstCell: {
    marginLeft: 15,
  },
  warningRow: {
    backgroundColor: ORANGE_LIGHTEST,
  },
  errorRow: {
    backgroundColor: RED_POWDER,
  },
  ignoreRow: {
    color: GRAY_DARK,
  },
  toggleIcon: {
    color: Colors.GRAY_DARKER,
    marginRight: 2,
    flex: 'none',
  },
  parentToggle: {
    cursor: 'pointer',
  },
  noToggle: {
    marginLeft: 15,
  },
  childRow: {
    backgroundColor: Colors.GRAY_LIGHTER,
    border: 'none',
    paddingLeft: '28px',
  },
  childActionCol: {
    marginLeft: '-28px',
  },
  subLabel: {
    display: 'inline-block',
    minWidth: '0',
    marginLeft: 28,
  },
  subLabelHeight: {
    lineHeight: '14px',
  },
  highImpactParentIcon: {
    marginLeft: 6,
  },
});
